import { useState } from 'react';
import { lt, useFeatureFlag } from '@mews/framework';
import { DialogFormProps, useDialogForm } from '@mews-commander/form';
import { OptimusProvider } from '@mews-commander/ui';
import { WithAccountId } from '@mews-commander/types';
import { SimpleAmount, useRepositoryQuery } from '@mews-commander/data-access';
import { Button, Disableable } from '@optimus-web/core';
import { FeatureFlag } from '@mews-commander/enums';
import { ProcessPaymentDialogContent } from './ProcessPaymentDialogContent';
import { ProcessPaymentType } from './ProcessPaymentType';
import { DefaultSelectedFormValues, ReadOnlyFormFields } from './useDefaultFormValues';
import { DEFAULT_PROCESS_PAYMENT_TYPES, DISABLED_PROCESS_PAYMENT_TYPES } from './const';
import { getPaymentTypeDataTestId, getPaymentTypesEnabledSetting } from './utils';
import { PaymentRequestType } from './PaymentRequestForm/schema';

export interface UseProcessPaymentDialogProps
    extends Omit<DialogFormProps, 'active' | 'renderForm' | 'id' | 'submitButtonDataTestId'>,
        WithAccountId {
    onFormSuccess?: () => void;
    billId?: string;
    allowedPaymentTypes?: ProcessPaymentType[];
    canLinkInvoices?: boolean;
    defaultSelectedFormValues?: DefaultSelectedFormValues;
    readOnlyFormFields?: ReadOnlyFormFields;
    paymentMethodCardsOptions?: ProcessPaymentDialogPaymentMethodCardsOptions;
    hideSinglePaymentMethodTitle?: boolean;
    allowedPaymentRequestTypeOptions?: PaymentRequestType[];
    billAmount?: SimpleAmount;
}

export interface ProcessPaymentDialogPaymentMethodCardsOptions {
    onlyCardsForMultipleMethods?: boolean;
    onlyDescriptionForSingleMethod: boolean;
}

export const useProcessPaymentDialog = ({
    accountId,
    onFormSuccess,
    allowedPaymentTypes = DEFAULT_PROCESS_PAYMENT_TYPES,
    title = lt('ProcessPaymentPanelTitleForOptions'),
    ...props
}: UseProcessPaymentDialogProps) => {
    const [paymentType, setPaymentType] = useState<ProcessPaymentType>();
    const [renderDialog, toggleDialog] = useDialogForm({
        title,
        ...props,
        renderForm: ({ formRef, onSuccess, onSubmitting, onSubmitted }) => (
            <ProcessPaymentDialogContent
                {...props}
                allowedPaymentTypes={allowedPaymentTypes}
                currentFormRef={formRef}
                accountId={accountId}
                onFormSuccess={() => {
                    onFormSuccess?.();
                    onSuccess?.();
                }}
                onFormSubmitting={() => {
                    onSubmitting?.();
                }}
                onFormSubmitted={() => {
                    onSubmitted?.();
                }}
                onChangePaymentType={setPaymentType}
            />
        ),
        renderPrimaryAction: ({ handleSubmit, disabled }) => (
            <ProcessPaymentDialogSubmitButton
                accountId={accountId}
                handleSubmit={handleSubmit}
                disabled={disabled}
                paymentType={paymentType}
            />
        ),
    });

    const renderProcessPaymentDialog = () => <OptimusProvider density="spacious">{renderDialog()}</OptimusProvider>;
    return [renderProcessPaymentDialog, toggleDialog] as const;
};

interface ProcessPaymentDialogSubmitButtonProps extends WithAccountId, Required<Disableable> {
    paymentType: ProcessPaymentType | undefined;
    handleSubmit: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const ProcessPaymentDialogSubmitButton = ({
    accountId,
    handleSubmit,
    disabled,
    paymentType,
}: ProcessPaymentDialogSubmitButtonProps) => {
    const { data, isFetching } = useRepositoryQuery('paymentMethods', 'getAvailability', { accountId });
    const paymentTypesEnabledSetting = getPaymentTypesEnabledSetting(data);
    const isPciProxyMaintenanceDisabledPaymentsEnabled = useFeatureFlag(
        FeatureFlag.pciProxyMaintenanceDisabledPaymentsEnabled
    );
    const disabledPaymentType = paymentType
        ? !paymentTypesEnabledSetting[paymentType] ||
          (isPciProxyMaintenanceDisabledPaymentsEnabled && DISABLED_PROCESS_PAYMENT_TYPES.includes(paymentType))
        : true;

    return (
        <Button
            variant="primary"
            onClick={handleSubmit}
            data-test-id={getPaymentTypeDataTestId(paymentType)}
            disabled={disabledPaymentType || disabled}
            loading={isFetching}
            type="submit"
        >
            {lt('Submit')}
        </Button>
    );
};
